import Vue from 'vue'
import App from './App.vue'
import './_plugins/registerServiceWorker'
import router from './_router'
import store from './_store'
import vuetify from './_plugins/vuetify'
import '@/_styles/main.scss'
import draggable from "vuedraggable";
import { Container, Draggable } from "vue-smooth-dnd";
import VueClipboard from 'vue-clipboard2'
import i18n from '@/_plugins/i18n'
import authService from '@/_services/auth-service'
import appConfig from '@/apps/gimo/app.config'

Vue.config.productionTip = false
Vue.component('container',Container);
Vue.component('draggable',Draggable);
Vue.use(VueClipboard);

// ============================================================================
// AUTHENTICATION BEFORE APP MOUNT
// ============================================================================
// Get OAuth settings from the app config
const clientId = appConfig.settings?.clientId;
const tokenUrl = appConfig.settings?.tokenUrl;
const scope = appConfig.settings?.scope;
const authorizeUrl = appConfig.settings?.authorizeUrl;
const logoutUrl = appConfig.settings?.logoutUrl;

if (!clientId || !tokenUrl || !scope || !authorizeUrl) {
  console.error('Available config:', appConfig?.settings);
  document.body.innerHTML = '<div style="padding: 20px; font-family: sans-serif;"><h1>Configuration Error</h1><p>OAuth settings are not configured. Please check apps/gimo/app.config.js</p></div>';
  throw new Error('Missing OAuth configuration');
}
// Configure auth service
authService.configure({
  clientId,
  scope,
  authorizeUrl,
  tokenUrl,
  logoutUrl,
  redirectUri: window.location.origin,
});
// Function to mount the Vue app
function mountApp() {
  
  // Start token refresh monitoring
  authService.startRefreshMonitoring(clientId, tokenUrl, scope);
  new Vue({
    router,
    store,
    vuetify,
    i18n,

    render: h => h(App),
    created()
    {
      if(this.appInsights)
      {
        this.appInsights.loadAppInsights();
        this.appInsights.trackPageView();
      }
    }
  }).$mount('#app')
  
  // Set up hash navigation listener after app is mounted
  // This handles when users paste URLs into the address bar
  // We need to force re-navigation because Vue Router might have already updated the route
  // but the component hasn't re-rendered with new params
  window.addEventListener('hashchange', () => {
    const newHash = window.location.hash.slice(1); // Remove the '#'
    
    if (newHash) {
      // Force navigation - this will cause the component to re-render even if on same route with different params
      router.replace(newHash).catch(err => {
        // Ignore navigation duplicated errors
        if (err.name !== 'NavigationDuplicated') {
          console.error('[Main] Navigation error:', err);
        }
      });
    }
  });
}

// Check if this is an OAuth callback
if (authService.isCallback()) {
  
  // Check for errors in callback
  if (authService.hasCallbackError()) {
    const error = authService.getCallbackError();
    console.error('[Main] OAuth error:', error.error, error.errorDescription);
    
    // Show error to user with retry option
    document.body.innerHTML = `
      <div style="padding: 40px; font-family: sans-serif; max-width: 600px; margin: 0 auto;">
        <h1 style="color: #d32f2f;">Authentication Error</h1>
        <p><strong>Error:</strong> ${error.error}</p>
        <p><strong>Description:</strong> ${error.errorDescription || 'Unknown error'}</p>
        <button onclick="location.href='/'" style="padding: 10px 20px; font-size: 16px; cursor: pointer; background: #1976d2; color: white; border: none; border-radius: 4px;">
          Try Again
        </button>
      </div>
    `;
  } else {
    // Handle successful callback
    authService.handleCallback()
      .then((originalPath) => {
        // Extract hash portion from original URL or default to home
        // originalPath is full URL like: https://gpugimo-stg.lyondellbasell.com/#/gimo/ChooseOperation
        // We need to extract the hash and redirect with hash prefix
        let targetPath = '/';
        if (originalPath) {
          const hashIndex = originalPath.indexOf('#');
          if (hashIndex !== -1) {
            // Extract everything after # (including the /)
            targetPath = originalPath.substring(hashIndex); // e.g., "/#/category"
          }
        }
        
        // Redirect to hash-mode URL (e.g., "/#/category")
        // This ensures nginx/web server routes correctly
        window.location.href = window.location.origin + targetPath;
      })
      .catch((error) => {
        console.error('[Main] Callback processing failed:', error);
        document.body.innerHTML = `
          <div style="padding: 40px; font-family: sans-serif; max-width: 600px; margin: 0 auto;">
            <h1 style="color: #d32f2f;">Authentication Failed</h1>
            <p>${error.message}</p>
            <button onclick="location.href='/'" style="padding: 10px 20px; font-size: 16px; cursor: pointer; background: #1976d2; color: white; border: none; border-radius: 4px;">
              Try Again
            </button>
          </div>
        `;
      });
  }
} else {
  // Not a callback - check if authenticated
  
  
  // Check if we just came back from a logout (no query params but credentials might still exist)
  // Clear all session storage flags to allow fresh login
  const hasStoredCredentials = !!localStorage.getItem('credential-cache');
  if (hasStoredCredentials) {
    // Validate the credentials
    if (!authService.isAuthenticated(clientId)) {
      // Clear everything to ensure fresh start
      authService.deleteCredentials(clientId);
      sessionStorage.clear();
    }
  }
  
  const isAuth = authService.isAuthenticated(clientId);
  
  if (isAuth) {
    mountApp();
  } else {
    // Not authenticated - redirect to login
    authService.redirectToLogin();
  }
}
